import { Collection, Flex } from "@aws-amplify/ui-react"
import { bslVertriebUserId } from "@strtmn/sds/config"
import type { Device, MachineSupaDto } from "@strtmn/sds/types"
import { useContext } from "react"
import { FaChartSimple, FaRegBell, FaTractor } from "react-icons/fa6"
import { Link, useRouteLoaderData, type LoaderFunction } from "react-router-dom"
import { listVehicles } from "../../api/VehiclesApi"
import { ConnectivityIndicator } from "../../components"
import { isProd } from "../../config"
import { UserDataContext, UserDataContextHelper } from "../../contexts"
import { useActiveOrgId } from "../../hooks"
import {
  getLinkToVehicleEuDataAct,
  getLinkToVehicleOverview,
} from "../../LinkUtils"
import { FilterAndSort } from "../orgs/-components/FilterAndSort"
import { ListItem, StarStates } from "../orgs/-components/ListItem"

export type EnrichedMachineSupa = MachineSupaDto & {
  vehicleFromDdb: Device | undefined
}

function renderConnectivityStatus(item: Device): React.ReactNode {
  let dataDisabled = true
  let dataConnected = false
  const dataPresence = item["presence"]["data"]["eventType"] || "unknown"
  if ("unknown" !== dataPresence) {
    dataDisabled = false
    if ("connected" === dataPresence) {
      dataConnected = true
    }
  }

  let ajaDisabled = true
  let ajaConnected = false
  const ajaPresence = item["presence"]["aja"]["eventType"] || "unknown"
  if ("unknown" !== ajaPresence) {
    ajaDisabled = false
    if ("connected" === ajaPresence) {
      ajaConnected = true
    }
  }

  return (
    <span
      style={{
        color: "#242424",
      }}
    >
      <ConnectivityIndicator
        name="Daten"
        isConnected={dataConnected}
        isDisabled={dataDisabled}
      />
      <ConnectivityIndicator
        name="Fernwartung"
        isConnected={ajaConnected}
        isDisabled={ajaDisabled}
      />
    </span>
  )
}

function renderFunc(
  enrichedMachineSupa: EnrichedMachineSupa,
  hasViewButton: boolean
) {
  const vehicleFromDdb = enrichedMachineSupa.vehicleFromDdb
  const vin = enrichedMachineSupa.serial_number || vehicleFromDdb?.vin
  const modem_sn = enrichedMachineSupa.modem_serial || vehicleFromDdb?.modem_sn
  const machineType: string =
    enrichedMachineSupa.machine_type || vehicleFromDdb?.["m_machineType"] || ""
  const machineTypeLc = machineType.toLowerCase()
  const connectivityStatus = vehicleFromDdb ? (
    renderConnectivityStatus(vehicleFromDdb)
  ) : (
    <ConnectivityIndicator
      isDisabled={true}
      isConnected={false}
      name="x-no-device-data"
    />
  )
  const euDataActLink = getLinkToVehicleEuDataAct({ vin })
  const details = (
    <>
      <div className="mt-1 mb-2">
        <span className="pr-2">{connectivityStatus}</span>
        {!enrichedMachineSupa.hasActiveDnv && (
          <Link
            className="!no-underline"
            to={euDataActLink}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="error p-1 !border-0">
              <FaRegBell className="inline" /> Es liegt keine DNV vor
            </span>
          </Link>
        )}
      </div>
      {machineType || "Experiment"}
      {/* <br />
      {item["adresse"] ? item["adresse"].ort : "Ort: nicht angegeben"} */}
      <br />
      Modem S/N: {modem_sn}
      <br />
    </>
  )
  let imageName: string = "unknown-machine-type.png"
  if (machineTypeLc === "primus") {
    // explicit fall-back for machine_type "Primus"
    imageName = "primus-600.jpg"
  } else if (
    machineTypeLc.startsWith("p600") ||
    machineTypeLc.startsWith("p617") ||
    machineTypeLc.startsWith("p620") ||
    machineTypeLc.startsWith("primus600")
  ) {
    imageName = "primus-600.jpg"
  } else if (
    machineTypeLc.startsWith("p900") ||
    machineTypeLc.startsWith("primus900")
  ) {
    imageName = "primus-900.jpg"
  } else if (
    machineTypeLc.startsWith("everti") ||
    machineTypeLc.startsWith("e-verti")
  ) {
    imageName = "e-vertifeed.jpg"
  } else if (
    machineTypeLc.startsWith("eprimus") ||
    machineTypeLc.startsWith("e-primus")
  ) {
    imageName = "e-primus.jpg"
  }
  const imageSrc = imageName && `/images/${imageName}`
  return (
    <ListItem
      key={vin}
      className="vehicle"
      title={vin}
      href={hasViewButton ? getLinkToVehicleOverview({ vin }) : undefined}
      imageSrc={imageSrc}
      details={details}
      starState={StarStates.hidden}
      viewButtonIcon={<FaChartSimple />}
      hasViewButton={hasViewButton}
    />
  )
}

// Component is used at /vehicles
function VehiclesListPage() {
  const { userData } = useContext(UserDataContext)
  const activeOrgId = useActiveOrgId()

  const title = "Meine Fahrzeuge"
  const icon = <FaTractor className="w-5 _h-3.5_ relativ text-[#3E9E29]" />

  const vehiclesFromDdb = useRouteLoaderData("vehicles-page") as Device[]
  !isProd &&
    console.debug(
      `${vehiclesFromDdb.length} vehicles from loader:`,
      vehiclesFromDdb
    )

  let filteredItems: EnrichedMachineSupa[] = []
  if (activeOrgId && userData) {
    const org = UserDataContextHelper.findOrg(activeOrgId, userData)
    const machinesFromUserCtx = org.Machines
    !isProd &&
      console.debug(
        `${machinesFromUserCtx.length} machines from userData:`,
        machinesFromUserCtx
      )

    // Filtering is not required, as the list from UserCtx is already for the current org.
    // const filteredItems = filterDevices(items, activeOrgId)

    // Merge data from supa (machine meta data) and ddb (presence)
    filteredItems = machinesFromUserCtx.map((supa) => {
      // Match against `seriennummer`, as this should be unique, and m_fahrgestellnummer is not
      const matchingVehicle = vehiclesFromDdb.find(
        (device) =>
          device["seriennummer"] ===
          supa.modem_serial /*||device["m_fahrgestellnummer"] === supa.serial_number*/
      )
      return {
        ...supa,
        vehicleFromDdb: matchingVehicle,
      }
    })

    // TODO: Sort
    // const aBeforeB = -1
    // const bBeforeA = 1
    // data.sort((a, b) => {
    //   /** Sort obsolete entries to the end of the list */
    //   const obsoleteIndicators = ["kahu-", "evnet-", "_old", "_alt"]
    //   const aSeriennummer = (
    //     ((a["seriennummer"] as string) + "#" + a["ext_sn"]) as string
    //   ).toLocaleLowerCase()
    //   const bSeriennummer = (
    //     ((b["seriennummer"] as string) + "#" + b["ext_sn"]) as string
    //   ).toLocaleLowerCase()
    //   const isAObsolete = obsoleteIndicators.some((indicator) =>
    //     aSeriennummer.includes(indicator)
    //   )
    //   const isBObsolete = obsoleteIndicators.some((indicator) =>
    //     bSeriennummer.includes(indicator)
    //   )
    //   if (isAObsolete && !isBObsolete) {
    //     return bBeforeA
    //   }
    //   if (!isAObsolete && isBObsolete) {
    //     return aBeforeB
    //   }
    //   /** Sort machines with real VIN to the top of the list */
    //   const aHasRealVin = (a["m_fahrgestellnummer"] as string).startsWith("W")
    //   const bHasRealVin = (b["m_fahrgestellnummer"] as string).startsWith("W")
    //   if (aHasRealVin && !bHasRealVin) {
    //     return aBeforeB
    //   }
    //   if (!aHasRealVin && bHasRealVin) {
    //     return bBeforeA
    //   }
    //   return 0
    // })
  }
  const _className = "vehicles"

  // hide ViewButton, depending on user id
  const userId = userData?.id ?? ""
  const showViewButtons = userId !== bslVertriebUserId

  // derived from ListPage
  return (
    <div className="rm-list-page">
      <Flex className="!h-12 !pb-2 !justify-between !items-center">
        <Flex className="!h-6 !justify-start !items-center gap-3">
          {icon}
          <div className="text-xl font-semibold">{title}</div>
        </Flex>

        {/* <Flex className="!justify-center !items-center gap-2.5">
          <Link to='new'>
            <Button className="button-add has-icon"><span role="img" aria-label="add"><FaPlus /></span></Button>
          </Link>
        </Flex> */}
      </Flex>

      <div className="rm-list-container">
        <FilterAndSort />

        <Collection
          type="list"
          items={filteredItems}
          className={`rm-list ${_className}`}
        >
          {(enrichedMachineSupa) =>
            renderFunc(enrichedMachineSupa, showViewButtons)
          }
        </Collection>
      </div>
    </div>
  )
}

// Loader is used at /vehicles and /vehicles/:vin/debug
/** @todo Move into a DataLoader / Repository class */
export const loader: LoaderFunction = async (): Promise<Device[]> => {
  console.log("> VehiclesListPage.loader")

  try {
    const vehicles = await listVehicles()
    if (!vehicles) {
      throw new Error("User is not authorized.")
    }
    return vehicles
  } catch (err) {
    console.error("VehiclesListPage.loader: Error fetching data from api")
    // Maybe setError, or throw to let ErrorBoundary do the work...
  }
  return []
}

export const Component = VehiclesListPage
